// src/pages/dataInput/BusinessFilesField.tsx
import React, { useRef } from "react";
import { cls } from "../theme/theme";
import { useBusinessFiles, PendingFile } from "../hooks/useBusinessFiles";
import { BusinessFileManifestEntry } from "../API";

interface Props {
  /** When appName is provided, saved files are fetched and shown. */
  appName: string;
  /**
   * Called after every successful upload so Step 2 knows files are ready.
   * The parent doesn't need to store File objects — the hook manages state.
   */
  onFilesChange?: (savedCount: number) => void;
}

const FILE_ICON: Record<string, string> = {
  ".pdf":  "📕",
  ".docx": "📘", ".doc": "📘",
  ".xlsx": "📗", ".xls": "📗",
  ".csv":  "📊",
  ".txt":  "📄", ".md": "📄",
  ".pptx": "📙", ".ppt": "📙",
};

const fileIcon = (name: string) => {
  const ext = name.substring(name.lastIndexOf(".")).toLowerCase();
  return FILE_ICON[ext] ?? "📄";
};

const fmt_bytes = (b: number) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const fmt_date = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString([], {
      month: "short", day: "numeric",
    });
  } catch {
    return "";
  }
};

export const BusinessFilesField: React.FC<Props> = ({ appName, onFilesChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    saved, pending,
    loading, uploading, error,
    addFiles, removePending, uploadPending, deleteSaved,
  } = useBusinessFiles(appName || undefined);

  // Notify parent whenever saved count changes
  React.useEffect(() => {
    onFilesChange?.(saved.length);
  }, [saved.length, onFilesChange]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length) {
      addFiles(files);
    }
  };

  const hasPending = pending.length > 0;
  const hasContent = saved.length > 0 || hasPending;

  return (
    <div className="gw-form-group">
      {/* Section header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 10,
      }}>
        <div>
          <label className="gw-label" style={{ marginBottom: 2 }}>
            Business Reference Documents
            <span style={{
              fontFamily: "'Sora', system-ui, sans-serif",
              fontWeight: 400,
              color: "#6b7280",
              marginLeft: 6,
              fontSize: 10,
              letterSpacing: 0,
              textTransform: "none",
            }}>
              optional
            </span>
          </label>
          <div style={{ fontSize: 11, color: "#6b7280", lineHeight: 1.4 }}>
            Glossaries, data dictionaries, governance policies, or domain
            standards used as LLM context during enrichment.
          </div>
        </div>
        {loading && (
          <div style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10,
            color: "#6b7280",
          }}>
            Loading…
          </div>
        )}
      </div>

      {/* Drop zone */}
      <label
        className={cls("gw-dropzone", hasContent && "has-files")}
        onDragOver={e => e.preventDefault()}
        onDrop={handleDrop}
        style={{
          minHeight: hasContent ? "auto" : 110,
          padding: hasContent ? "14px 18px" : "24px 18px",
          cursor: "pointer",
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.doc,.txt,.md,.xlsx,.xls,.csv,.pptx,.ppt"
          style={{ display: "none" }}
          onChange={e => {
            const files = Array.from(e.target.files ?? []);
            if (files.length) addFiles(files);
            // Reset so same file can be re-added
            e.target.value = "";
          }}
        />

        {!hasContent ? (
          <>
            <div className="gw-dropzone-icon">📚</div>
            <div className="gw-dropzone-label">
              Drag &amp; drop business reference files, or click to browse
            </div>
            <div className="gw-dropzone-sub">
              PDF · DOCX · XLSX · CSV · TXT · MD — up to 25 MB each
            </div>
          </>
        ) : (
          <div style={{ width: "100%" }}>
            {/* Saved files */}
            {saved.map((f: BusinessFileManifestEntry) => (
              <div
                key={f.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 10px",
                  borderRadius: 8,
                  background: "#f0fdf4",
                  border: "1px solid #86efac",
                  marginBottom: 6,
                }}
                onClick={e => e.preventDefault()}
              >
                <span style={{ fontSize: 18, flexShrink: 0 }}>{fileIcon(f.name)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: "#166534",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}>
                    {f.name}
                  </div>
                  <div style={{
                    fontSize: 10.5,
                    color: "#16a34a",
                    fontFamily: "'JetBrains Mono', monospace",
                    marginTop: 1,
                  }}>
                    {fmt_bytes(f.size_bytes)} · uploaded {fmt_date(f.uploaded_at)}
                  </div>
                </div>
                <div
                  className="gw-mode-badge enriched"
                  style={{ flexShrink: 0 }}
                >
                  saved
                </div>
                <button
                  type="button"
                  title="Delete"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    deleteSaved(f.name);
                  }}
                  style={{
                    all: "unset",
                    cursor: "pointer",
                    color: "#16a34a",
                    fontSize: 13,
                    padding: "0 4px",
                    borderRadius: 4,
                    lineHeight: 1,
                    flexShrink: 0,
                  }}
                >
                  ✕
                </button>
              </div>
            ))}

            {/* Pending files */}
            {pending.map((p: PendingFile) => (
              <div
                key={p.file.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 10px",
                  borderRadius: 8,
                  background:
                    p.status === "error" ? "#fef2f2" :
                    p.status === "done"  ? "#f0fdf4" :
                    p.status === "uploading" ? "#dde7f5" :
                    "#fff7ed",
                  border:
                    p.status === "error" ? "1px solid #fca5a5" :
                    p.status === "done"  ? "1px solid #86efac" :
                    p.status === "uploading" ? "1px solid #90aed0" :
                    "1px solid #fde68a",
                  marginBottom: 6,
                }}
                onClick={e => e.preventDefault()}
              >
                <span style={{ fontSize: 18, flexShrink: 0 }}>{fileIcon(p.file.name)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color:
                      p.status === "error" ? "#991b1b" :
                      p.status === "done"  ? "#166534" :
                      "#0f1923",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}>
                    {p.file.name}
                  </div>
                  <div style={{
                    fontSize: 10.5,
                    color: "#6b7280",
                    fontFamily: "'JetBrains Mono', monospace",
                    marginTop: 1,
                  }}>
                    {fmt_bytes(p.file.size)}
                    {p.status === "uploading" && " · uploading…"}
                    {p.status === "error" && (
                      <span style={{ color: "#991b1b" }}> · {p.error}</span>
                    )}
                  </div>
                </div>
                <div
                  className={cls(
                    "gw-mode-badge",
                    p.status === "error"     ? "rejected" :
                    p.status === "done"      ? "enriched" :
                    p.status === "uploading" ? "raw"      :
                    "typed"
                  )}
                  style={{ flexShrink: 0 }}
                >
                  {p.status === "uploading" ? "uploading" :
                   p.status === "done"      ? "done" :
                   p.status === "error"     ? "error" :
                   "staged"}
                </div>
                {p.status === "pending" && (
                  <button
                    type="button"
                    title="Remove"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      removePending(p.file.name);
                    }}
                    style={{
                      all: "unset",
                      cursor: "pointer",
                      color: "#6b7280",
                      fontSize: 13,
                      padding: "0 4px",
                      flexShrink: 0,
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}

            {/* Add more link */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 8,
                paddingTop: 8,
                borderTop: "1px dashed #c8d4e0",
              }}
              onClick={e => e.preventDefault()}
            >
              <button
                type="button"
                className="gw-btn gw-btn-ghost gw-btn-sm"
                onClick={e => {
                  e.preventDefault();
                  inputRef.current?.click();
                }}
              >
                + Add more
              </button>
            </div>
          </div>
        )}
      </label>

      {/* Upload button — only shows when there are staged files */}
      {hasPending && (
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 8,
          padding: "10px 14px",
          borderRadius: 8,
          background: "#fff7ed",
          border: "1px solid #fde68a",
        }}>
          <div style={{ fontSize: 12.5, color: "#7c2d00" }}>
            <strong>{pending.filter((p:any) => p.status === "pending").length}</strong> file
            {pending.filter((p:any) => p.status === "pending").length === 1 ? "" : "s"} staged
            — not yet saved to the server.
          </div>
          <button
            type="button"
            className="gw-btn gw-btn-primary gw-btn-sm"
            onClick={e => {
              e.preventDefault();
              uploadPending();
            }}
            disabled={uploading}
            style={{ flexShrink: 0 }}
          >
            {uploading ? "Uploading…" : "Upload now →"}
          </button>
        </div>
      )}

      {error && (
        <div style={{
          marginTop: 6,
          fontSize: 12,
          color: "#991b1b",
          padding: "6px 10px",
          borderRadius: 6,
          background: "#fef2f2",
          border: "1px solid #fca5a5",
        }}>
          {error}
        </div>
      )}

      {!appName?.trim() && (
        <div style={{
          marginTop: 6,
          fontSize: 11.5,
          color: "#6b7280",
          fontStyle: "italic",
        }}>
          Enter an application name above to enable file upload.
        </div>
      )}
    </div>
  );
};

export default BusinessFilesField;